import {exists} from "../../utils/helpers.js";

/**
 * Resolve parentName when saving a field edit.
 * linkedGroup is context for picking a component — not the direct parent when a field lives in one.
 * @param {object} builder
 * @param {object} targetElement
 * @param {{linkedComponent?: string, linkedGroup?: string}} linkValues
 * @returns {string}
 */
export const resolveFieldParentName = (builder, targetElement, linkValues) => {
  const {linkedComponent, linkedGroup} = linkValues;
  const currentParent =
    targetElement.parentName && targetElement.parentName in builder.byId
      ? builder.byId[targetElement.parentName]
      : null;

  if (linkedComponent && linkedComponent !== "") return linkedComponent;

  if (linkedGroup && linkedGroup !== "") {
    if (currentParent?.element === "component") {
      if (linkedComponent === "") return linkedGroup;
      return targetElement.parentName || "";
    }
    return linkedGroup;
  }

  return targetElement.parentName || "";
};

/* Transform formula through recursion to get base level formula (no generated fields as arguments)
 * @param {array} currentFormula
 * @param {object} builder
 * @returns {array}
 */
export const transformFormula = (currentFormula, builder) => {
  let lim = currentFormula.length;
  const temp = [...currentFormula];
  for (let i = 0; i < lim; i++) {
    // Case of primary field dependency (first item in depends) being a generated field
    if (
      temp[i].value in builder.byId &&
      builder.byId[temp[i].value].type === "generated" &&
      !temp[i].previous
    ) {
      const element = builder.byId[temp[i].value];
      // recurse until hitting base level (number field)
      const transformed = transformFormula(element.formula, builder);
      const toSplice = [
        {label: "(", value: "(", type: "grouping", start: true, formulaName: element.name},
        ...transformed,
        {label: ")", value: ")", type: "grouping", end: true, formulaName: element.name}
      ];
      temp.splice(i, 1, ...toSplice);
      lim = temp.length;
    }
  }
  return temp;
};

/**
 * Build formula string for evaluation
 * @param {array} currentFormula
 * @param {object} builder
 * @param {bool} absoluteValue
 * @returns {string}
 */
export const buildFormulaString = (currentFormula, builder, absoluteValue) => {
  let formulaString = "result=";
  if (absoluteValue) formulaString = `${formulaString}abs(`;
  if (currentFormula) {
    for (let i = 0; i < currentFormula.length; i++) {
      const f = currentFormula[i];
      let fieldName = f.value;

      const hasAbsoluteValue =
        f?.formulaName &&
        (builder.byId[f.formulaName]?.absoluteValue ||
          builder.byId[f.formulaName]?.trueDepends?.filter(
            key => builder.byId[key]?.type === "time"
          )?.length > 0);

      if (f.start && hasAbsoluteValue) formulaString = `${formulaString}abs(`;
      if (f.previous) fieldName = `${fieldName}_previous`;
      if (fieldName.includes("Other: "))
        formulaString = `${formulaString}Decimal(${fieldName.replace("Other: ", "")})`;
      else formulaString = `${formulaString}${fieldName}`;

      if (f.end && hasAbsoluteValue) formulaString = `${formulaString})`;
    }
    if (absoluteValue) formulaString = `${formulaString})`;
  }
  return formulaString;
};

/**
 * Returns formatted formula field object to be saved into builder
 * @param {object} builder
 * @param {string} mode
 * @param {object} targetElement
 * @param {object} [values]
 * @returns {object}
 */
export const formatFormulaToSave = (builder, mode, targetElement, values = null) => {
  const coalescedValues = values || targetElement || {};
  const {formula, absoluteValue} = coalescedValues;

  let depends;
  let trueDepends;
  let valuesWithLinkedFields;

  const {
    linkedComponent: newParentComponent,
    linkedGroup: newParentGroup,
    divideByZeroDefault,
    hasARange,
    hasPrecision,
    precision
  } = coalescedValues;

  const hasDivideByZeroDefault =
    coalescedValues.hasDivideByZeroDefault || exists(coalescedValues.divideByZeroDefault);

  if (mode === "edit") {
    const nestedExpanded = transformFormula(formula, builder) ?? formula;
    depends = [];
    trueDepends = [];
    nestedExpanded
      .filter(({previous}) => !previous)
      .forEach(part => {
        if (
          part.type === "variable" &&
          part.value in builder.byId &&
          !trueDepends.includes(part.value)
        )
          trueDepends.push(part.value);
      });
    formula
      .filter(({previous}) => !previous)
      .forEach(part => {
        if (part.type === "variable" && part.value in builder.byId && !depends.includes(part.value))
          depends.push(part.value);
      });

    const elementParent = resolveFieldParentName(builder, targetElement, {
      linkedComponent: newParentComponent,
      linkedGroup: newParentGroup
    });

    valuesWithLinkedFields = {
      ...targetElement,
      ...coalescedValues,
      formula,
      formulaString: buildFormulaString(nestedExpanded, builder, absoluteValue),
      depends,
      trueDepends,
      trueFormula: nestedExpanded,
      parentName: elementParent,
      precision: hasPrecision ? precision : null,
      divideByZeroDefault: hasDivideByZeroDefault ? divideByZeroDefault : null,
      aMin: hasARange ? coalescedValues.aMin : null,
      aMax: hasARange ? coalescedValues.aMax : null
    };
  } else {
    depends = [];
    trueDepends = [];
    const temp = transformFormula(formula, builder) ?? formula;
    temp
      .filter(({previous}) => !previous)
      .forEach(part => {
        if (
          part.type === "variable" &&
          part.value in builder.byId &&
          !trueDepends.includes(part.value)
        )
          trueDepends.push(part.value);
      });
    formula
      .filter(({previous}) => !previous)
      .forEach(part => {
        if (part.type === "variable" && part.value in builder.byId && !depends.includes(part.value))
          depends.push(part.value);
      });
    valuesWithLinkedFields = {
      ...coalescedValues,
      formula,
      formulaString: buildFormulaString(temp, builder, absoluteValue),
      depends,
      trueDepends,
      parentName: targetElement.parentName,
      trueFormula: temp,
      precision: hasPrecision ? precision : null,
      divideByZeroDefault: hasDivideByZeroDefault ? divideByZeroDefault : null,
      aMin: hasARange ? coalescedValues.aMin : null,
      aMax: hasARange ? coalescedValues.aMax : null
    };
  }
  delete valuesWithLinkedFields.other;
  delete valuesWithLinkedFields.fieldToAdd;
  delete valuesWithLinkedFields.addMode;
  delete valuesWithLinkedFields.linkedComponent;
  delete valuesWithLinkedFields.linkedGroup;
  if (valuesWithLinkedFields.units && valuesWithLinkedFields.units.includes("Other: ")) {
    valuesWithLinkedFields.units = valuesWithLinkedFields.units.replace("Other: ", "");
  }
  return valuesWithLinkedFields;
};
